import _ from "lodash";
import PropTypes from "prop-types";
import React from "react";
import ReactECharts from 'echarts-for-react';
import { capitalizeFirstLetter } from '../../../app/utilities/helpers.js';

const DonutChart = (props) => {
    const {
        chartData = [],
        chartheight = false,
        chartType = "pie",
        colors,
        events,
        graphicText,
        innerRadius = "40%",
        labelConfig,
        labelLine = true,
        labelLinelength = 15,
        labelShow = true,
        legendData = [],
        legendhover = true,
        legendhoverOffset = false,
        legendleft = "left",
        legendSelection,
        legendshow,
        midfontSize = 16,
        midTitleName,
        onChartClick,
        onChartDrillClick,
        onLegendSelectChanged,
        onSprintClick,
        outerRadius = "60%",
        percentage = 0,
        position = "outside",
        selectedMode = true,
        showLegend = true,
        showPercentage = false,
        showTitle = false,
        spaceBottom = "50%",
        titleName,
        toolTipConfig,
    } = props;

    let data;

    const secondGraphicText = (parseFloat(graphicText) + 6) + '%';
    const updatedData = legendData.map(label => {
        const existingItem = chartData.find(item => item[label] !== undefined);

        if (!existingItem) {
            return {
                [label]: "0"
            };
        }
        const updatedItem = {
            ...existingItem,
            [label]: String(existingItem[label])
        };

        return updatedItem;
    });

    if (!Array.isArray(updatedData)) {
        data = updatedData;
    } else {
        data = updatedData.map((item) => {
            const [label, value] = Object.entries(item)[0];
            return parseInt(value);
        });
    }

    const totalValue = data ? Object.values(data).reduce((sum, value) => sum + value, 0) : "";

    const totalPercentage = Math.round(percentage);

    const legendHandler = React.useMemo(() => {
        if (!legendData || !legendData.length) return {};
        const selection = {};
        legendData.forEach(name => {
            selection[name] = legendSelection?.[name] ?? true;
        });
        return selection;
    }, [legendData, legendSelection]);

    const getPieOption = () => {
        const options = {
            tooltip: toolTipConfig ? { ...toolTipConfig } : {
                show: true,
                trigger: 'item',
                confine: true,
                alwaysShowContent: false,
                formatter: function (params) {
                    return `
                    <div style="width: 100%;">
                        <table style="width: 100%; border-spacing: 0; border-collapse: collapse;">
                          <tr>
                            <td style="text-align: left;">${params.marker} ${params.name} </td>
                        <td style="text-align: right;"><b>${params.value.toLocaleString()}</b></td>
                          </tr>
                        </table>
                      </div>`;
                }
            },
            title: [
                {
                    show: showTitle,
                    text: titleName,
                    x: "center",
                    y: 30,
                    textStyle: {
                        fontSize: 15,
                    }
                },
            ],

            legend: {
                orient: "horizontal",
                left: legendleft,
                type: "scroll",
                top: "top",
                show: showLegend ? showLegend : legendshow,
                data: legendData || (data ? Object.keys(data) : []),
                icon: 'circle',
                selectedMode: selectedMode,
                itemWidth: 13,
                itemHeight: 13,
                emphasis: {
                    itemStyle: {
                        emphasis: false,
                        focus: "none"
                    },
                },
                selected: legendHandler,
            },
            grid: {
                left: '20%',
                right: '20%',
                top: '20%',
                bottom: '20%',
            },
            graphic: {
                elements: [{
                    type: 'text',
                    left: 'center',
                    top: graphicText ? graphicText : '48%',
                    z: 999,
                    style: {
                        text: showPercentage === true ? `${totalPercentage}%` : midTitleName ? `${totalPercentage}` : `Total ${totalValue}`,
                        textAlign: 'center',
                        fontSize: midfontSize,
                    }
                },
                {
                    type: 'text',
                    left: 'center',
                    top: secondGraphicText ? secondGraphicText : 'middle',
                    z: 999,
                    style: {
                        text: capitalizeFirstLetter(midTitleName), // Display the title here
                        textAlign: 'bottom',
                        fontSize: midTitleName ? midTitleName : 16,
                        color: 'gray', // You can customize the color
                    },
                },]
            },
            series: [{
                type: chartType,
                center: ['50%', spaceBottom],

                labelLine: {
                    show: labelLine,
                    length: labelLinelength,
                },
                hoverAnimation: legendhover,
                hoverOffset: legendhoverOffset === true ? 0 : undefined,
                hoverLink: legendhover,
                radius: [(innerRadius), (outerRadius)],
                data: data ? Object.keys(data).map((key, index) => ({
                    name: legendData && legendData[index] ? legendData[index] : key,
                    value: data[key] !== 0 ? data[key] : null,
                    itemStyle: {
                        color: colors && colors[index] ? colors[index] : undefined,
                    }
                })) : "",
                label: labelConfig ? { ...labelConfig } : {
                    show: labelShow,
                    position: (position),
                    formatter: '{d}%',

                },

            }]
        };
        options.series[0].onclick = handleChartClick;
        return options;
    };

    let chartInstance = null;

    const handleChartClick = (params) => {
        const dataIndex = params.componentIndex;
        if (params.componentType === "series" || params.componentType === "graphic") {
            const clickedSprint = titleName;

            if (onSprintClick) {
                onSprintClick(clickedSprint, dataIndex);
            }
            if (onChartClick) {
                onChartClick(params);
            }
            if (chartInstance) {
                chartInstance.dispatchAction({
                    type: 'hideTip'
                });
            }
            if (onChartDrillClick) {
                let lastPart = params.name.toLowerCase();
                const taskType = lastPart === 'option' ? 'Total Items' : lastPart.charAt(0) + lastPart.slice(1);
                const idValue = params.dataIndex;
                const formattedId = taskType === "ProdFix" ? "prodFix" : taskType;
                onChartDrillClick(titleName[0]?.status, formattedId, idValue, "Flow Load (Active Sprint)");
            }
        }
    };

    const handleChartReady = (chart) => {
        chartInstance = chart;
    };

    const handleLegendSelectChanged = (params) => {

        if (onLegendSelectChanged) {
            onLegendSelectChanged(params.selected);
        }
        const selectedLegends = params.selected;
        const sumOfTrueLegends = showPercentage ? null : Object.keys(selectedLegends).filter(legend => selectedLegends[legend]).reduce((sum, legend) => sum + parseInt(updatedData.find(item => item[legend])[legend]), 0);
        const updatedOption = {
            graphic: {
                elements: [{
                    type: 'text',
                    left: 'center',
                    top: graphicText ? graphicText : 'middle',
                    z: 999,
                    style: {
                        text: showPercentage === true ? `${totalPercentage}%` : `Total ${sumOfTrueLegends.toString()}`,
                        textAlign: 'center',
                        fontSize: midfontSize,
                    }
                }]
            }
        };

        if (chartInstance) {
            chartInstance.setOption(updatedOption);
        }

    };

    const onEvents = {
        'click': handleChartClick,
        'legendselectchanged': handleLegendSelectChanged,

    };

    return (
        <div style={{ height: chartheight === true ? '24vw' : null }}>
            <ReactECharts option={getPieOption()} onEvents={events ? onEvents : ""} onChartReady={handleChartReady} />
        </div>
    );
};


function areEqual(prevProps, nextProps) {
    if (_.isEqual(prevProps.chartData, nextProps.chartData) &&
        _.isEqual(prevProps.colors, nextProps.colors) &&
        _.isEqual(prevProps.legendData, nextProps.legendData) &&
        _.isEqual(prevProps.innerRadius, nextProps.innerRadius) &&
        _.isEqual(prevProps.outerRadius, nextProps.outerRadius) &&
        _.isEqual(prevProps.position, nextProps.position) &&
        _.isEqual(prevProps.labelShow, nextProps.labelShow) &&
        _.isEqual(prevProps.showLegend, nextProps.showLegend) &&
        _.isEqual(prevProps.legendleft, nextProps.legendleft) &&
        _.isEqual(prevProps.legendshow, nextProps.legendshow) &&
        _.isEqual(prevProps.showTitle, nextProps.showTitle) &&
        _.isEqual(prevProps.titleName, nextProps.titleName) &&
        _.isEqual(prevProps.percentage, nextProps.percentage) &&
        _.isEqual(prevProps.midTitleName, nextProps.midTitleName) &&
        _.isEqual(prevProps.countTitle, nextProps.countTitle) &&
        _.isEqual(prevProps.midfontSize, nextProps.midfontSize) &&
        _.isEqual(prevProps.showPercentage, nextProps.showPercentage) &&
        _.isEqual(prevProps.graphicText, nextProps.graphicText) &&
        _.isEqual(prevProps.selectedMode, nextProps.selectedMode) &&
        _.isEqual(prevProps.legendhover, nextProps.legendhover) &&
        _.isEqual(prevProps.events, nextProps.events) &&
        _.isEqual(prevProps.legendhoverOffset, nextProps.legendhoverOffset) &&
        _.isEqual(prevProps.isScroll, nextProps.isScroll) &&
        _.isEqual(prevProps.countTitle, nextProps.countTitle) &&
        _.isEqual(prevProps.chartDataList, nextProps.chartDataList) &&
        _.isEqual(prevProps.spaceLeft, nextProps.spaceLeft) &&
        _.isEqual(prevProps.spaceRight, nextProps.spaceRight) &&
        _.isEqual(prevProps.spaceTop, nextProps.spaceTop) &&
        _.isEqual(prevProps.spaceBottom, nextProps.spaceBottom) &&
        _.isEqual(prevProps.valuType, nextProps.valuType)) {
        return true;
    } else {
        return false;
    }
}

DonutChart.propTypes = {
    chartData: PropTypes.array.isRequired,
    chartheight: PropTypes.bool,
    chartType: PropTypes.string,
    colors: PropTypes.array,
    events: PropTypes.bool,
    graphicText: PropTypes.string,
    innerRadius: PropTypes.string,
    labelConfig: PropTypes.object,
    labelLine: PropTypes.bool,
    labelLinelength: PropTypes.number,
    labelShow: PropTypes.bool,
    legendData: PropTypes.array,
    legendhover: PropTypes.bool,
    legendhoverOffset: PropTypes.bool,
    legendleft: PropTypes.string,
    legendSelection: PropTypes.object,
    legendshow: PropTypes.bool,
    midfontSize: PropTypes.number,
    midTitleName: PropTypes.string,
    onChartClick: PropTypes.func,
    onChartDrillClick: PropTypes.func,
    onLegendSelectChanged: PropTypes.func,
    onSprintClick: PropTypes.func,
    outerRadius: PropTypes.string,
    percentage: PropTypes.number,
    position: PropTypes.string,
    selectedMode: PropTypes.bool,
    showLegend: PropTypes.bool,
    showPercentage: PropTypes.bool,
    showTitle: PropTypes.bool,
    spaceBottom: PropTypes.string,
    titleName: PropTypes.string,
    toolTipConfig: PropTypes.object,
};

export default React.memo(DonutChart, areEqual);